<template>
  <div class="app-header">
    <div class="header-left">
      <n-button text @click="toggleSidebar" v-if="authStore.isAuthenticated" class="toggle-btn">
        <n-icon size="20"><Menu /></n-icon>
      </n-button>
      
      <n-breadcrumb>
        <n-breadcrumb-item @click="router.push('/')">首页</n-breadcrumb-item>
        <n-breadcrumb-item v-for="item in breadcrumbItems" :key="item.path" @click="router.push(item.path)">
          {{ item.title }}
        </n-breadcrumb-item>
      </n-breadcrumb>
    </div>

    <div v-if="authStore.orgImpersonating" class="header-center">
      <div class="org-proxy-status">
        <span class="org-proxy-text">
          当前代管理状态 代登人：<strong>{{ orgProxyOperatorName }}</strong>
          <template v-if="orgProxyShopLabel"> · 店铺 {{ orgProxyShopLabel }}</template>
          <template v-if="authStore.orgImpersonation?.org_name">
            · {{ authStore.orgImpersonation.org_name }}
          </template>
        </span>
        <n-button size="tiny" type="warning" ghost :loading="exitingOrg" @click="handleOrgExit">
          退出代登
        </n-button>
      </div>
    </div>

    <div class="header-actions">
      <template v-if="authStore.isAuthenticated">
        <n-badge
          v-if="showMessageBell"
          :value="unreadCount"
          :max="99"
          :show="unreadCount > 0"
          style="margin-right: 16px;"
        >
          <n-button text @click="goMessages" style="font-size: 20px;">
            <n-icon size="22"><NotificationsOutline /></n-icon>
          </n-button>
        </n-badge>
        <n-dropdown @select="handleCommand" :options="dropdownOptions">
          <div class="user-dropdown">
            <n-avatar :size="32" color="#409EFF">{{ userInitial }}</n-avatar>
            <span class="user-name">{{ authStore.user?.real_name || authStore.user?.username || '用户' }}</span>
          </div>
        </n-dropdown>
      </template>
      <n-button v-else type="primary" @click="handleLogin">登录</n-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, h, inject, ref, watch, onUnmounted } from 'vue'
import { NBreadcrumb, NBreadcrumbItem, NDropdown, NAvatar, NButton, NIcon, NBadge } from 'naive-ui'
import { LogOutOutline as LogOut, MenuOutline as Menu, NotificationsOutline } from '@vicons/ionicons5'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const { logout } = useAuth()

const sidebarCollapsed = inject('sidebarCollapsed', ref(false))
const unreadCount = ref(0)

const showMessageBell = computed(() => {
  if (!authStore.orgImpersonating) return true
  return authStore.hasOrgPerm('message.view')
})

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

const goMessages = () => {
  router.push('/messages')
}

// 轮询未读消息数量
let unreadTimer: ReturnType<typeof setInterval> | null = null

const fetchUnreadCount = async () => {
  // 排除认证相关页面，避免在登录流程中触发请求
  if (!authStore.isAuthenticated || route.path.startsWith('/auth/')) return
  if (!showMessageBell.value) return
  try {
    const res = await $fetch('/api/messages/unread-count') as any
    if (res.success) {
      unreadCount.value = res.data.count
    }
  } catch {
    // 忽略异常
  }
}

// 监听认证状态，登录后才开始轮询
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth && !route.path.startsWith('/auth/')) {
    fetchUnreadCount()
    if (!unreadTimer) {
      unreadTimer = setInterval(fetchUnreadCount, 60000)
    }
  } else if (!isAuth) {
    unreadCount.value = 0
    if (unreadTimer) {
      clearInterval(unreadTimer)
      unreadTimer = null
    }
  }
}, { immediate: true })

onUnmounted(() => {
  if (unreadTimer) clearInterval(unreadTimer)
})

const userInitial = computed(() => {
  const name = authStore.user?.real_name || authStore.user?.username || '用户'
  return name.charAt(0).toUpperCase() || 'U'
})

const orgProxyOperatorName = computed(() => {
  const info = authStore.orgImpersonation
  return info?.member_real_name || info?.member_username || '未知'
})

const orgProxyShopLabel = computed(() => {
  const info = authStore.orgImpersonation
  return info?.shop_name || (info?.shop_id != null ? String(info.shop_id) : '')
})

const exitingOrg = ref(false)
async function handleOrgExit() {
  exitingOrg.value = true
  try {
    const res = await $fetch<{ success: boolean; data?: { redirect?: string } }>(
      '/api/org-impersonate/exit',
      { method: 'POST' }
    )
    authStore.clearAuth()
    const redirect = res.data?.redirect || '/partner/'
    window.location.href = redirect
  } catch (e: any) {
    console.error(e)
    window.location.href = '/partner/'
  } finally {
    exitingOrg.value = false
  }
}

const breadcrumbItems = computed(() => {
  const items = []
  const path = route.path
  
  if (path.startsWith('/advertisers')) {
    items.push({ title: '店铺管理', path: '/advertisers' })
    if (path.includes('/campaigns')) {
      items.push({ title: '计划管理', path: path })
    }
  } else if (path.startsWith('/auth/qianchuan')) {
    items.push({ title: '千川授权', path: '/auth/qianchuan' })
  }
  
  return items
})

const dropdownOptions = [
  {
    label: '退出登录',
    key: 'logout',
    icon: () => h(NIcon, null, { default: () => h(LogOut) })
  }
]

const handleLogin = () => {
  // 直接使用 window.location 确保页面完全刷新
  // 这样可以避免 Vue 组件缓存导致页面内容不更新的问题
  if (true) {
    window.location.href = '/auth/login'
  } else {
    navigateTo('/auth/login')
  }
}

const handleCommand = (key: string) => {
  if (key === 'logout') logout()
}
</script>

<style scoped>
.app-header {
  position: relative;
  height: 60px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-bottom: 1px solid #e0e0e6;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  max-width: min(640px, 52vw);
  z-index: 1;
}

.org-proxy-status {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 12px;
  font-size: 13px;
  color: #ad6800;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  max-width: 100%;
}

.org-proxy-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.toggle-btn {
  font-size: 20px;
}

.header-actions {
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 4px;
}

.user-dropdown:hover {
  background: #f5f7fa;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
}
</style>
